import { FormikProps } from "formik";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import {
  useGetBankListQuery,
  useResolveAccountDetailsMutation,
} from "../../redux/features/payment/payment.api";
import { PayoutAccountPayload } from "../../redux/features/payment/payment.types";
import { PRIMARY_COLOR } from "../../utils/constants";
import { BaseApiErrorResponse } from "../../utils/types";
import ComboBox from "../dropdown/comboBox";
import { Icon } from "../icon/icon";
import { TextInput } from "../inputs/textInput";
import { LoadingOval } from "../loaders/loaders";

const handleSearch = async (searchFunction: () => void) => {
  await searchFunction();
};

const handleDebouncedSearch = debounce(handleSearch, 800);

export const NGNPayoutForm = ({
  formik,
  setDisable,
}: {
  formik: FormikProps<PayoutAccountPayload & { confirm: string }>;
  setDisable: (val: boolean) => void;
}) => {
  const [bankSearch, setBankSearch] = useState("");

  const { data: bankList } = useGetBankListQuery({});

  const [
    resolveAccount,
    {
      data: accountDetails,
      isSuccess: resolveSuccess,
      isError: resolveError,
      error: resolveErrorDetails,
      isLoading: resolvingAccount,
    },
  ] = useResolveAccountDetailsMutation();

  const { values, touched, handleBlur, handleChange, errors, setFieldValue } =
    formik;

  useEffect(() => {
    if (!!values.accountNumber && !!values.bankCode) {
      handleDebouncedSearch(async () => {
        const resp = await resolveAccount({
          accountNumber: values.accountNumber,
          bankCode: values.bankCode ?? "",
        });

        if (resp.data?.status === "success") {
          setFieldValue("accountName", resp.data.data.accountName);
          setFieldValue("accountNumber", resp.data.data.accountNumber);
          setFieldValue("bankCode", resp.data.data.bankCode);
        } else {
          setFieldValue("accountName", undefined);
        }
      });
    }
    // eslint-disable-next-line
  }, [values.accountNumber, values.bankCode]);

  useEffect(() => {
    setDisable(resolvingAccount);
    // eslint-disable-next-line
  }, [resolvingAccount]);

  return (
    <>
      <ComboBox
        menuItems={bankList?.data
          ?.filter((bank) =>
            bankSearch !== ""
              ? bank.name.toLowerCase().includes(bankSearch.toLowerCase())
              : true,
          )
          ?.map((bank) => {
            return {
              name: bank.name,
              value: { name: bank.name, code: bank.code },
            };
          })}
        position={"bottom start"}
        menuClassName="translate-x-[-12px] !h-[224px] !z-[999]"
        menuItemsClassName=""
        onSearch={(search) => {
          setBankSearch(search);
        }}
        onSelectOption={(val: { name: string; code: string }) => {
          setFieldValue("bankName", val.name);
          setFieldValue("bankCode", val.code);
        }}
        onMenuClose={() => {
          setBankSearch("");
        }}
        immediate
        type="text"
        name="bankName"
        value={values.bankName}
        placeholder="Select Bank"
        onChange={(e) => {
          handleChange(e);
          setFieldValue("bankCode", "");
        }}
        onBlur={handleBlur}
        hasTitle
        title="Bank"
        readOnly={false}
        customIconPosition="right"
        errors={
          (errors.bankName && touched.bankName ? errors.bankName : "") ||
          (errors.bankCode && touched.bankName ? errors.bankCode : "")
        }
        inputClass="capitalize"
      />

      <TextInput
        type="text"
        name="accountNumber"
        value={values.accountNumber}
        placeholder="Account Number"
        onChange={handleChange}
        onBlur={handleBlur}
        hasTitle
        title="Account Number"
        errors={
          (errors.accountNumber && touched.accountNumber
            ? errors.accountNumber
            : undefined) ??
          (resolveError
            ? (resolveErrorDetails as unknown as BaseApiErrorResponse)?.data
                ?.message
            : undefined)
        }
        customIconPosition="right"
        customIcon={
          resolvingAccount ? (
            <LoadingOval
              color={PRIMARY_COLOR}
              loaderWidth="20"
              loaderHeight="20"
            />
          ) : undefined
        }
      />

      {resolveSuccess && (
        <TextInput
          type="text"
          name="accountName"
          value={accountDetails.data.accountName}
          placeholder="Account Name"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Account Name"
          customIconPosition="right"
          customIcon={
            <Icon
              className="text-success"
              svg="circle-check"
              width={20}
              height={20}
            />
          }
          readOnly
        />
      )}
    </>
  );
};
